import {
  Download,
  Image as ImageIcon,
  Sparkles,
  Copy,
  Check,
  Linkedin,
  Instagram,
  ChevronUp,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

export default function ContentCard({
  item,
  index,
  darkMode,
  copiedId,
  copy,
  expanded,
  toggle,
  generatingImages,
  generateImage,
  exportPost,
  hookLabels,
}) {
  // Resolve hook variant style/text from either {style, text} or {styleName: text} format
  const resolveHook = (h) => {
    if (h.style && h.text) return { style: h.style, text: h.text };
    const key = Object.keys(h)[0];
    return { style: key, text: h[key] };
  };

  return (
    <div
      className={`rounded-xl overflow-hidden ${
        darkMode
          ? "bg-[#161b22] border border-[#30363d]"
          : "bg-white border border-gray-100 shadow-sm"
      }`}
      data-testid={`content-card-${index}`}
    >
      {/* Header */}
      <div className={`px-5 py-4 border-b ${darkMode ? "border-[#21262d]" : "border-gray-100"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0">{item.topic_category}</Badge>
            <span className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
              {new Date(item.created_at).toLocaleDateString()}
            </span>
          </div>
          <Button
            onClick={() => exportPost(item.id)}
            variant="outline"
            size="sm"
            className={`gap-1.5 ${
              darkMode
                ? "border-[#30363d] text-gray-300 hover:bg-[#21262d] hover:text-white"
                : ""
            }`}
            data-testid={`export-btn-${index}`}
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
        <h2 className={`font-semibold mt-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
          {item.topic}
        </h2>
      </div>

      {/* Generated Image */}
      {item.image_url ? (
        <div className="relative">
          <img src={item.image_url} alt={item.topic} className="w-full h-64 object-cover" />
          <div className="absolute top-3 right-3">
            <Badge className="bg-black/60 text-white border-0 backdrop-blur-sm">
              <ImageIcon className="w-3 h-3 mr-1" />
              Generated
            </Badge>
          </div>
        </div>
      ) : (
        <div
          className={`relative h-48 flex items-center justify-center ${
            darkMode ? "bg-[#21262d]" : "bg-gray-50"
          }`}
        >
          <div className="text-center">
            <ImageIcon
              className={`w-10 h-10 mx-auto mb-3 ${
                darkMode ? "text-gray-600" : "text-gray-400"
              }`}
            />
            <Button
              onClick={() => generateImage(item.id)}
              disabled={generatingImages.has(item.id)}
              size="sm"
              className="bg-[#DC143C] hover:bg-[#b91031] text-white"
              data-testid={`generate-image-${index}`}
            >
              {generatingImages.has(item.id) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Image
                </>
              )}
            </Button>
            <p className={`text-xs mt-2 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
              AI-powered social media graphic
            </p>
          </div>
        </div>
      )}

      <div className="p-5 space-y-4">
        {/* Primary Post */}
        <div className={`p-4 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}>
          <div className="flex items-center justify-between mb-3">
            <span
              className={`text-xs font-medium flex items-center gap-1.5 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" /> Primary Post
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copy(item.primary_post, `p-${item.id}`)}
              className={`h-8 w-8 p-0 ${
                darkMode ? "text-gray-400 hover:text-white" : "text-gray-400"
              }`}
            >
              {copiedId === `p-${item.id}` ? (
                <Check className="w-4 h-4 text-emerald-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className={`text-sm ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
            {item.primary_post}
          </p>
          {item.cta && <p className="text-sm text-[#DC143C] font-medium mt-3">{item.cta}</p>}
        </div>

        {/* Hook Variants */}
        {item.hook_variants?.length > 0 && (
          <Collapsible open={expanded.has(item.id)}>
            <CollapsibleTrigger
              onClick={() => toggle(item.id)}
              className={`w-full flex items-center justify-between p-3 rounded-lg ${
                darkMode ? "bg-[#21262d] hover:bg-[#30363d]" : "bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {item.hook_variants.length} Hook Variants
              </span>
              {expanded.has(item.id) ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2">
              {item.hook_variants.map((h, hi) => {
                const { style, text } = resolveHook(h);
                const s = hookLabels[style] || {
                  label: style?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
                  color: "bg-gray-100 text-gray-600",
                };
                return (
                  <div
                    key={`${item.id}-hook-${style}-${hi}`}
                    className={`p-3 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${s.color} border-0 text-xs`}>{s.label}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copy(text, `h-${item.id}-${hi}`)}
                        className="h-7 w-7 p-0"
                      >
                        {copiedId === `h-${item.id}-${hi}` ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                    <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {text}
                    </p>
                  </div>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Platform Versions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {item.linkedin_version && (
            <div className={`p-3 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-blue-600 flex items-center gap-1">
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copy(item.linkedin_version, `li-${item.id}`)}
                  className="h-7 w-7 p-0"
                >
                  {copiedId === `li-${item.id}` ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
              <p className={`text-xs line-clamp-3 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                {item.linkedin_version}
              </p>
            </div>
          )}
          {item.instagram_version && (
            <div className={`p-3 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-pink-600 flex items-center gap-1">
                  <Instagram className="w-3.5 h-3.5" /> Instagram
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copy(item.instagram_version, `ig-${item.id}`)}
                  className="h-7 w-7 p-0"
                >
                  {copiedId === `ig-${item.id}` ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
              <p className={`text-xs line-clamp-3 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                {item.instagram_version}
              </p>
            </div>
          )}
        </div>

        {/* Hashtags */}
        {item.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {item.hashtags.map((t, ti) => (
              <button
                key={`${item.id}-tag-${t}-${ti}`}
                onClick={() => copy(t, `t-${item.id}-${ti}`)}
                className={`px-2 py-1 rounded text-xs ${
                  darkMode
                    ? "bg-[#21262d] text-gray-400 hover:text-white hover:bg-[#30363d]"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {t.startsWith("#") ? t : `#${t}`}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
